import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Thermometer, Camera } from "lucide-react";
import { StatusPill } from "@/components/StatusPill";
import { LocationSelect, useLocations } from "@/components/LocationSelect";
import { EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtTemp } from "@/lib/format";

const nowHM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

export default function Controles() {
  const { activeLocation } = useLocations();
  const nav = useNavigate();
  const [controls, setControls] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [area, setArea] = useState("all");

  useEffect(() => {
    if (!activeLocation) { setControls([]); setLoading(false); return; }
    setLoading(true);
    setArea("all");
    api.get(`/controls/today?location_id=${activeLocation}`)
      .then(({ data }) => setControls(data)).catch(() => setControls([])).finally(() => setLoading(false));
    api.get(`/shifts?location_id=${activeLocation}`).then(({ data }) => setShifts(data)).catch(() => setShifts([]));
  }, [activeLocation]);

  const currentShift = useMemo(() => {
    const hm = nowHM();
    const sorted = [...shifts].filter((s) => s.active !== false)
      .sort((a, b) => (a.reference_start_time || "").localeCompare(b.reference_start_time || ""));
    let cur = null;
    for (const s of sorted) { if ((s.reference_start_time || "00:00") <= hm) cur = s; }
    if (!cur && sorted.length) cur = sorted[0];
    return cur;
  }, [shifts]);

  // Base list = controls of the current shift (fallback to all today's controls)
  const baseList = useMemo(
    () => (currentShift ? controls.filter((c) => c.shift_id === currentShift.id) : controls),
    [controls, currentShift]
  );

  const pending = baseList.filter((c) => ["PENDING", "LATE"].includes(c.status)).length;

  // Areas dynamically derived from the controls of the current local/shift
  const areas = useMemo(() => {
    const map = new Map();
    baseList.forEach((c) => { if (c.area_id && !map.has(c.area_id)) map.set(c.area_id, c.area_name || "Área"); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [baseList]);

  const visible = area === "all" ? baseList : baseList.filter((c) => c.area_id === area);

  const goRegister = (c) => {
    nav("/app/registro", { state: {
      equipment: {
        id: c.equipment_id, name: c.equipment_name,
        min_temperature: c.min_temperature, max_temperature: c.max_temperature,
        unit: c.unit, location_name: c.location_name, area_name: c.area_name,
      },
      shift_id: c.shift_id, expected_control_id: c.id,
    }});
  };

  return (
    <div className="px-4 pt-5">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-[#153D35]">Control de temperaturas</h1>
      <p data-testid="temp-summary" className="mt-1 text-sm text-slate-500">
        {loading ? "Cargando…" : `${pending} pendiente${pending === 1 ? "" : "s"}${currentShift ? ` · ${currentShift.name}` : ""}`}
      </p>
      <div className="mt-3"><LocationSelect /></div>

      {areas.length > 0 && (
        <div className="mt-4 -mx-4 overflow-x-auto px-4 no-scrollbar">
          <div className="flex gap-2">
            <AreaChip label="Todos" active={area === "all"} onClick={() => setArea("all")} testid="area-chip-all" />
            {areas.map((a) => (
              <AreaChip key={a.id} label={a.name} active={area === a.id} onClick={() => setArea(a.id)} testid={`area-chip-${a.id}`} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-400">Cargando…</div>
        ) : visible.length === 0 ? (
          <EmptyState icon={Thermometer} title="Sin equipos" subtitle={currentShift ? `No hay equipos para controlar en ${currentShift.name}.` : "No hay controles programados hoy para este local."} />
        ) : (
          visible.map((c) => <EquipmentCard key={c.id} control={c} onRegister={() => goRegister(c)} />)
        )}
      </div>
    </div>
  );
}

function AreaChip({ label, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
        active ? "border-[#2563EB] bg-[#2563EB] text-white" : "border-[#E2E8F0] bg-white text-[#68756F] hover:border-[#2563EB]/40"
      }`}
    >
      {label}
    </button>
  );
}

function EquipmentCard({ control: c, onRegister }) {
  const done = c.status === "COMPLETED" || c.status === "INCIDENT";
  const title = c.equipment_code ? `${c.equipment_code} — ${c.equipment_name}` : c.equipment_name;
  const range = (c.min_temperature != null && c.max_temperature != null)
    ? `${fmtTemp(c.min_temperature, c.unit)} a ${fmtTemp(c.max_temperature, c.unit)}` : null;
  return (
    <div data-testid={`control-card-${c.equipment_id}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-[#153D35]">{title}</p>
          <p className="mt-0.5 truncate text-xs text-[#68756F]">
            {[c.area_name, c.equipment_type_name].filter(Boolean).join(" · ") || "—"}
          </p>
          {range && <p className="mt-1 text-xs text-slate-400">Rango: <span className="tnum">{range}</span></p>}
          <div className="mt-2 flex items-center gap-2">
            <StatusPill status={c.status} />
            {done && c.temperature != null && <span className="tnum text-sm font-medium text-slate-700">{fmtTemp(c.temperature, c.unit)}</span>}
          </div>
        </div>
        {!done && (
          <Button data-testid={`register-btn-${c.equipment_id}`} onClick={onRegister} className="h-11 shrink-0 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Camera className="mr-1.5 h-4 w-4" /> Registrar
          </Button>
        )}
      </div>
    </div>
  );
}
